/**
 * POST MedicationDispense via EHRPoster.
 * Default: POST individual por recurso (servidor atual).
 * Bundle transaction: ligar USE_MEDICATION_DISPENSE_BUNDLE_POST.
 * validUntil = extension valueDate; recorded = emissão.
 * @module PostMedicationDispense
 */

import type { Bundle, MedicationDispense } from "fhir/r5";
import { endpoints } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import {
  buildLocalPurchaseReleaseBatch,
  buildMedicationDispenseTransactionBundle,
} from "src-next/modules/patient-supporting-documents/purchase-release/buildMedicationDispenseDraft";
import { USE_MEDICATION_DISPENSE_BUNDLE_POST } from "src-next/modules/patient-supporting-documents/purchase-release/constants";
import type {
  BuildPurchaseReleaseBatchParams,
  LocalPurchaseReleaseBatch,
} from "src-next/modules/patient-supporting-documents/purchase-release/types";
import { PURCHASE_RELEASE_BATCH_EXTENSION_URL } from "src-next/modules/patient-supporting-documents/purchase-release/constants";

function extractIdFromLocation(location?: string): string | undefined {
  if (!location) return undefined;
  const parts = location.split("/");
  const last = parts[parts.length - 1]?.split("?")[0]?.trim();
  return last || undefined;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Accept: "application/fhir+json",
    Authorization: (await useAuth.getUserToken()) ?? "",
    "Content-Type": "application/fhir+json",
  };
}

function withBatchId(
  resource: MedicationDispense,
  batchId: string,
): MedicationDispense {
  return {
    ...resource,
    extension: [
      ...(resource.extension ?? []).filter(
        (e) => e.url !== PURCHASE_RELEASE_BATCH_EXTENSION_URL,
      ),
      {
        url: PURCHASE_RELEASE_BATCH_EXTENSION_URL,
        valueString: batchId,
      },
    ],
  };
}

/** Um POST por MedicationDispense em /MedicationDispense. */
async function postMedicationDispenseIndividually(
  params: BuildPurchaseReleaseBatchParams,
): Promise<LocalPurchaseReleaseBatch> {
  const draft = buildLocalPurchaseReleaseBatch(params);
  const headers = await authHeaders();
  const url = `${endpoints.EHRPOSTER}/MedicationDispense`;

  const results = await Promise.allSettled(
    draft.dispenses.map(async (resource) => {
      const body = withBatchId(resource, draft.batchId);
      // Sem id local — servidor gera
      const { id: _omit, ...toPost } = body as MedicationDispense & {
        id?: string;
      };
      void _omit;

      const response = await useAxios.post(url, toPost, {
        headers,
        timeout: 60000,
      });
      if (
        response.status !== HTTPResponseStatus.Created &&
        response.status !== HTTPResponseStatus.Success
      ) {
        throw new Error(`PostMedicationDispense: ${response.status}`);
      }

      const locationHeader =
        (response.headers?.location as string | undefined) ||
        (response.headers?.Location as string | undefined);
      const id =
        (response.data as MedicationDispense | undefined)?.id ||
        extractIdFromLocation(locationHeader);

      return withBatchId({ ...body, ...(id ? { id } : {}) }, draft.batchId);
    }),
  );

  const persisted: MedicationDispense[] = [];
  let failed = 0;
  for (const result of results) {
    if (result.status === "fulfilled") {
      persisted.push(result.value);
    } else {
      failed += 1;
      console.error("PostMedicationDispense individual failed", result.reason);
    }
  }

  if (!persisted.length) {
    throw new Error("Nenhuma liberação foi gravada.");
  }
  if (failed > 0) {
    throw new Error(
      `${failed} de ${results.length} produtos não foram gravados.`,
    );
  }

  return { ...draft, dispenses: persisted };
}

/** Bundle transaction — reativar com USE_MEDICATION_DISPENSE_BUNDLE_POST. */
async function postMedicationDispenseBundle(
  params: BuildPurchaseReleaseBatchParams,
): Promise<LocalPurchaseReleaseBatch> {
  const draft = buildLocalPurchaseReleaseBatch(params);
  const body = buildMedicationDispenseTransactionBundle(
    params,
    draft.batchId,
  );
  const headers = await authHeaders();

  const response = await useAxios
    .post(`${endpoints.EHRPOSTER}/`, body, { headers, timeout: 60000 })
    .then((res) => {
      if (
        res.status !== HTTPResponseStatus.Created &&
        res.status !== HTTPResponseStatus.Success
      ) {
        throw new Error(`PostMedicationDispense: ${res.status}`);
      }
      return res.data as Bundle;
    })
    .catch((error) => {
      console.error("PostMedicationDispense bundle ", error);
      throw new Error();
    });

  const requestResources =
    body.entry?.map((e) => e.resource as MedicationDispense) ?? [];

  const persisted: MedicationDispense[] = (response.entry ?? []).map(
    (entry, index) => {
      const id = extractIdFromLocation(entry.response?.location);
      const base = requestResources[index] ?? draft.dispenses[index];
      return withBatchId({ ...base, ...(id ? { id } : {}) }, draft.batchId);
    },
  );

  return {
    ...draft,
    dispenses: persisted.length ? persisted : draft.dispenses,
  };
}

/**
 * Persiste N MedicationDispense (um por produto) no EHRPoster.
 * Retorna lote de cache UI com ids reais da response.
 */
export async function PostMedicationDispense(
  params: BuildPurchaseReleaseBatchParams,
): Promise<LocalPurchaseReleaseBatch> {
  if (!params.lines.length) {
    throw new Error("Inclua ao menos um produto na liberação.");
  }

  if (USE_MEDICATION_DISPENSE_BUNDLE_POST) {
    return postMedicationDispenseBundle(params);
  }
  return postMedicationDispenseIndividually(params);
}
